<template>
  <div id="SessionBooking" :style="cssVars">
    <div v-if="isSelectedViewLoading" class="d-flex justify-content-center">
      <Loader />
    </div>

    <div v-else>
      <div v-if="isProgressBarShown" class="progress-bar-container">
        <div class="progress-bar-outer w-100">
          <div
            class="progress-bar-inner"
            :style="{
              width: progressWidth + '%',
            }"
          ></div>
        </div>
      </div>

      <div
        v-if="selectedView === 'journey'"
        :key="currentQuestion && currentQuestion.question_id"
        class="dmh-start-container content-container"
      >
        <h2 v-if="isTitleShown" class="dmh-start-title">
          {{ currentQuestion.question_text || currentQuestion.sub_heading }}
        </h2>

        <p
          v-if="isSubHeadingShown"
          class="dmh-start-subheading"
          v-html="currentQuestion.sub_heading"
        ></p>

        <div class="dmh-options-container">
          <component
            :is="getComponent(questionType)"
            v-if="questionType"
            :question="currentQuestion"
            :answer="currentAnswer"
            :setAnswer="setAnswer"
            :answerQuestion="answerQuestion"
            :bookingJourneyId="bookingJourneyId"
            :serviceType="serviceType"
            :questionHistory="questionHistory"
            :onEditQuestion="returnToQuestion"
            :onNavigate="onNavigate"
            :apiQuestionnaire="apiQuestionnaire"
            :apiUrl="apiUrl"
            :userId="userId"
            @skip="answerQuestion"
            @overrideAnswer="answerQuestion"
            @setGenderPreference="setGenderPreference"
            @uploading="isImageUploading = $event"
          />

          <p v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </p>

          <div
            class="d-flex justify-content-end align-items-center dmh-start-btn-container"
          >
            <!-- On the first question previous() would fall through to history.back(),
                 which walks an embedded host out of the journey, so it is only shown
                 when the host supplies onPreviousFromFirstQuestion. -->
            <b-button
              v-if="questionHistory.length > 0 || onPreviousFromFirstQuestion"
              @click="previous(questionHistory)"
              class="btn-previous"
              size="lg"
              squared
              :disabled="isImageUploading"
            >
              Previous
            </b-button>

            <b-button
              :disabled="
                currentAnswer === null ||
                !allAnswersValid ||
                isAnswerQuestionLoading ||
                isImageUploading
              "
              class="btn-continue"
              size="lg"
              squared
              @click="answerQuestion"
            >
              Continue
            </b-button>
          </div>

          <div
            class="warning-container"
            v-if="
              currentQuestion && currentQuestion.lower_alert && !hideWarning
            "
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h4 class="mb-0 pb-0 warning-title">
                  <i class="fas fa-exclamation-circle pr-2"></i>Important
                </h4>
              </div>
              <button
                class="border-0 bg-transparent p-0"
                @click="setHideWarning(true)"
              >
                <i class="fas fa-times clickable"></i>
              </button>
            </div>
            <div
              v-html="currentQuestion.lower_alert.text"
              class="warning-text"
            ></div>
          </div>
        </div>
      </div>

      <div v-else-if="selectedView === 'booking'">
        <component
          :is="getComponent('clinician')"
          :bookingJourneyId="bookingJourneyId"
          :genderPreference="genderPreference"
          :questionHistory="questionHistory"
          :reset="reset"
          :previous="getQuestion"
          :userId="userId"
          :serviceType="serviceType"
          :apiQuestionnaire="apiQuestionnaire"
          :onNavigate="onNavigate"
          :serviceData="serviceData"
          :bupaTouchLink="bupaTouchLink"
          :locale="locale"
          :returnButtonText="returnButtonText"
          :onReturn="onReturn"
          :apiUrl="apiUrl"
          :userTimezone="userTimezone"
          :termsUrl="termsUrl"
          :vpcTermsUrl="vpcTermsUrl"
          :eapPhoneNumbers="eapPhoneNumbers"
          @hideProgressBar="hideProgressBar"
        />
      </div>

      <div
        v-else-if="selectedView === 'financeComplete'"
        class="dmh-options-container content-container"
      >
        <div
          class="d-flex flex-column align-items-center justify-content-center dmh-complete-container"
        >
          <h3>Thank you!</h3>
          <p>
            Our Team will be in touch within 48 hours to confirm your
            appointment.
          </p>
          <b-button class="btn-home" size="lg" @click="navigate('/')"
            >Return to portal</b-button
          >
        </div>
      </div>

      <div
        v-else-if="selectedView === 'callbackComplete'"
        class="dmh-options-container content-container callback-complete-container"
      >
        <div
          class="d-flex flex-column align-items-center justify-content-center dmh-complete-container"
        >
          <i class="fa fa-check-circle" aria-hidden="true"></i>
          <h3 class="title">Thank you</h3>
          <p class="paragraph">
            Your form has been submitted. We will be in touch to arrange your
            consultation.
          </p>
          <b-button class="btn-home" size="lg" @click="navigate('/')"
            >Return to home</b-button
          >
        </div>
      </div>

      <div
        v-else-if="selectedView === 'callbackError'"
        class="dmh-options-container content-container callback-error-container"
      >
        <div
          class="d-flex flex-column align-items-center justify-content-center dmh-complete-container"
        >
          <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
          <h3 class="title">Something went wrong</h3>
          <p class="paragraph">
            We couldn't submit your form. Please try again later.
          </p>
          <b-button class="btn-home" size="lg" @click="navigate('/')"
            >Return to home</b-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import defaultComponents from "../defaultComponentMap";
import journeyMixin from "../mixins/journeyMixin";
import Loader from "./default-components/Loader.vue";
import { scrollToTop, isEmbedded } from "../utils/helpers";
export default {
  name: "SessionBooking",

  // Note: distinct from the `components` prop below, which is the host's
  // question-type override map.
  components: { Loader },

  mixins: [journeyMixin],

  props: {
    userId: { type: String, required: true },
    serviceType: { type: String, required: true },
    apiQuestionnaire: Object,
    apiUrl: { type: String, default: "" },
    // Passed straight through to the slot picker; see ClinicianSelection.
    userTimezone: { type: String, default: "" },

    onNavigate: Function,
    onLogout: Function,
    onPreviousFromFirstQuestion: { type: Function, default: null },

    theme: { type: Object, default: () => ({}) },
    fonts: { type: Object, default: () => ({}) },

    serviceData: { type: Object, default: () => ({}) },
    bupaTouchLink: { type: String, default: "" },
    locale: { type: String, default: "" },

    returnButtonText: { type: String, default: "Return to Home" },
    onReturn: { type: Function, default: null },
    termsUrl: { type: String, default: "" },
    vpcTermsUrl: { type: String, default: "" },
    eapPhoneNumbers: { type: Array, default: () => [] },

    components: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      selectedView: "journey",
      isSelectedViewLoading: false,
      selectedOptionQId: null,
      selectedOptionIndex: null,
      currentQuestion: null,
      currentAnswer: null,
      questionHistory: [],
      progressWidth: 0,
      hideWarning: false,
      totalNumberOfQuestions: null,
      numberOfQuestionsToComplete: null,
      isProgressBarShown: true,
      isAnswerQuestionLoading: false,
      questionType: null,
      genderPreference: "No Preference",
      isImageUploading: false,
      answeredQuestions: [],
      previousQuestion: null,
      errorMessage: "",
      errorTimeout: null,
      bookingJourneyId: null,
      returnToReviewQuestionId: null,
      reviewPredecessorQuestionId: null,
    };
  },

  computed: {
    isTitleShown() {
      return (
        (this.currentQuestion?.question_text ||
          this.currentQuestion?.sub_heading) &&
        this.questionType !== "gp_override" &&
        this.questionType !== "location_detection"
      );
    },

    isSubHeadingShown() {
      return (
        this.currentQuestion?.question_text && this.currentQuestion?.sub_heading
      );
    },

    isTextInputQuestion() {
      return (
        this.currentQuestion?.question_type === "text_input" ||
        this.currentQuestion?.question_type === "text_area"
      );
    },

    allAnswersValid() {
      if (
        this.currentQuestion?.question_type === "location_detection" ||
        this.currentQuestion?.question_type === "review" ||
        this.currentQuestion?.question_type === "slider"
      ) {
        return true;
      }
      if (this.currentQuestion?.question_type === "image_upload") {
        console.log(
          "Validating image upload question. Current answer:",
          this.currentAnswer,
        );
        return (
          this.currentAnswer?.length >=
          (this.currentQuestion?.validation?.min_number_of_files || 1)
        );
      }
      if (this.isTextInputQuestion) {
        return this.currentAnswer?.length > 4;
      }
      if (this.currentQuestion?.question_type === "multi_select") {
        return this.currentAnswer?.length > 0;
      }
      const validationResults = this.validateDmhAnswers(
        this.currentAnswer,
        this.currentQuestion,
        this.apiUrl,
      );
      return validationResults
        ? Object.values(validationResults).every((v) => v === true)
        : true;
    },

    cssVars() {
      const vars = {};
      const { primary, secondary, tertiary, dark } = this.theme || {};
      if (primary) vars["--brand-primary"] = primary;
      if (secondary) vars["--brand-secondary"] = secondary;
      if (tertiary) vars["--brand-tertiary"] = tertiary;
      if (dark) vars["--brand-dark"] = dark;

      const { regular, bold } = this.fonts || {};
      if (regular) vars["--font-regular"] = regular;
      if (bold) vars["--font-bold"] = bold;

      return vars;
    },
  },

  watch: {
    currentQuestion(val) {
      this.questionType = val?.question_type;
    },

    numberOfQuestionsToComplete(value) {
      if (!this.totalNumberOfQuestions) return;
      const bookClinicianStep = 1;
      const totalSteps = this.totalNumberOfQuestions + bookClinicianStep;
      const stepsToComplete = value + bookClinicianStep;
      this.progressWidth = ((totalSteps - stepsToComplete) / totalSteps) * 100;
    },
  },

  mounted() {
    if (!this.serviceType) {
      this.navigate("/");
      return;
    }
    this.bookingJourneyId = this.getFormattedTimestamp();
    this.getQuestion();
  },

  beforeDestroy() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  },

  methods: {
    navigate(path) {
      this.onNavigate?.(path);
    },

    async createCallbackCase() {
      try {
        const res = await this.apiQuestionnaire.createCallbackCase({
          user_id: this.userId,
          service_type: this.serviceType,
          booking_journey_id: this.bookingJourneyId,
        });
        return Boolean(res?.data?.success);
      } catch (err) {
        console.error(err);
        return false;
      }
    },

    setGenderPreference(option) {
      this.genderPreference = option;
    },

    setHideWarning(value) {
      this.hideWarning = value;
    },

    hideProgressBar() {
      this.isProgressBarShown = false;
    },

    reduceStepsToComplete() {
      this.numberOfQuestionsToComplete -= 1;
    },

    increaseStepsToComplete() {
      this.numberOfQuestionsToComplete += 1;
    },

    addStepsToComplete(num) {
      this.numberOfQuestionsToComplete += num;
      this.totalNumberOfQuestions += num;
    },

    async returnToQuestion(questionId) {
      const questionIndex = this.questionHistory.findIndex(
        (question) => question === questionId,
      );
      const questionToFetch = this.questionHistory.slice(0, questionIndex + 1);
      this.questionHistory.push(this.currentQuestion.question_id);
      await this.getQuestion(questionToFetch, questionId);
    },

    getComponent(type) {
      return this.components[type] || defaultComponents[type];
    },

    scrollToTop() {
      this.$nextTick(() => scrollToTop(this.$el));
    },

    previous(questionHistory) {
      if (!questionHistory || questionHistory.length === 0) {
        if (this.onPreviousFromFirstQuestion) {
          this.onPreviousFromFirstQuestion();
        } else if (typeof window !== "undefined" && window.history) {
          window.history.back();
        } else {
          this.navigate("/");
        }
      } else {
        if (this.currentQuestion?.question_type === "review") {
          this.returnToReviewQuestionId = this.currentQuestion.question_id;
          // The last item in questionHistory is the question directly before Review.
          // Only return to Review after answering that specific question.
          this.reviewPredecessorQuestionId =
            questionHistory[questionHistory.length - 1] ?? null;
        }
        this.getQuestion(questionHistory);
      }
    },

    reset() {
      this.selectedOptionIndex = null;
      this.selectedOptionQId = null;
      this.currentQuestion = null;
      this.questionHistory = [];
      this.currentAnswer = null;
      this.selectedView = "journey";
      this.totalNumberOfQuestions = null;
      this.numberOfQuestionsToComplete = null;
      this.progressWidth = 0;
      this.returnToReviewQuestionId = null;
      this.reviewPredecessorQuestionId = null;
      this.navigate("/");
    },

    async getQuestion(questionHistory, questionId) {
      try {
        let question_id;
        this.isSelectedViewLoading = true;
        if (questionHistory?.length === 0) {
          this.selectedOptionQId = null;
          this.selectedOptionIndex = null;
          this.selectedView = "journey";
          this.$emit("update-nav", "mh");
        } else if (questionHistory?.length > 0) {
          this.selectedView = "journey";
        }
        if (questionHistory) {
          question_id = questionHistory.pop();
        }
        if (questionId) {
          if (questionId === this.totalNumberOfQuestions) {
            this.$emit("update-nav", "finance");
          } else {
            this.$emit("update-nav", "mh");
          }
          if (questionId === "faq") {
            return this.navigate("/faqs");
          }
          if (questionId === "articles") {
            return this.navigate("/articles");
          }
          question_id = questionId;
        }

        const payload = {
          user_id: this.userId,
          question_id,
          service_type: this.serviceType,
          booking_journey_id: this.bookingJourneyId,
        };

        const res = await this.apiQuestionnaire.getQuestion(payload);

        if (res.data.success) {
          this.currentQuestion = { ...res.data.question };
          this.totalNumberOfQuestions =
            res.data.questionnaire_stage.total_number_of_questions;
          this.numberOfQuestionsToComplete =
            res.data.questionnaire_stage.number_of_questions_to_complete;

          if (questionId) {
            this.selectedView = "journey";
          }

          this.currentAnswer = this.computeInitialAnswer(res.data.question);
        } else if (res.data.err && res.data.err.includes("3994")) {
          this.onLogout?.();
          this.navigate("/login");
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isSelectedViewLoading = false;
      }
    },

    async answerQuestion() {
      if (this.isAnswerQuestionLoading) return;
      this.isAnswerQuestionLoading = true;

      if (isEmbedded()) {
        this.isSelectedViewLoading = true;
        this.scrollToTop();
      } 

      try {
        const payload = {
          user_id: this.userId,
          answered_question_id: this.currentQuestion.question_id,
          answer: this.currentAnswer,
          service_type: this.serviceType,
          booking_journey_id: this.bookingJourneyId,
        };

        const res = await this.apiQuestionnaire.answerQuestion(payload);
        this.isAnswerQuestionLoading = false;
        if (isEmbedded()) this.isSelectedViewLoading = false;

        if (res.data.success) {
          this.scrollToTop();
          this.hideWarning = false;
          this.questionHistory.push(this.currentQuestion.question_id);

          // If this answer was submitted on the question directly before Review
          // (the user went Previous from Review and is now continuing back to it),
          // return to Review rather than following the server's next-question response.
          if (
            this.returnToReviewQuestionId &&
            this.currentQuestion.question_id ===
              this.reviewPredecessorQuestionId
          ) {
            const reviewQuestionId = this.returnToReviewQuestionId;
            this.returnToReviewQuestionId = null;
            this.reviewPredecessorQuestionId = null;
            const reviewIndex = this.questionHistory.findIndex(
              (q) => q === reviewQuestionId,
            );
            const historyToReview =
              reviewIndex !== -1
                ? this.questionHistory.slice(0, reviewIndex + 1)
                : [...this.questionHistory, reviewQuestionId];
            await this.getQuestion(historyToReview, reviewQuestionId);
            return;
          }

          this.numberOfQuestionsToComplete =
            res.data.questionnaire_stage.number_of_questions_to_complete;
          this.totalNumberOfQuestions =
            res.data.questionnaire_stage.total_number_of_questions;

          if (res.data.question === null) {
            const questionnaireName = res.data.questionnaire_name;
            const isCallbackQuestionnaire =
              this.numberOfQuestionsToComplete === 0 &&
              questionnaireName?.endsWith("sf_callback");
            if (isCallbackQuestionnaire) {
              this.isAnswerQuestionLoading = true;
              const created = await this.createCallbackCase();
              this.isAnswerQuestionLoading = false;
              this.progressWidth = 100;
              this.isProgressBarShown = false;
              this.selectedView = created
                ? "callbackComplete"
                : "callbackError";
            } else if (this.serviceType === "legal") {
              this.progressWidth = 100;
              this.selectedView = "financeComplete";
            } else {
              this.selectedView = "booking";
            }
          } else {
            this.selectedView = "journey";
            this.previousQuestion = this.currentQuestion;
            this.currentQuestion = { ...res.data.question };
          }

          // Re-hydrate currentAnswer from previous_answer if available
          if (
            res.data.question?.previous_answer &&
            res.data.question?.question_type !== "confirm"
          ) {
            this.currentAnswer = res.data.question.previous_answer;
            return;
          } else if (
            res.data.question?.question_type !== "confirm" &&
            this.answeredQuestions.find(
              (item) => item.question_id === res.data.question.question_id,
            )
          ) {
            this.currentAnswer = this.answeredQuestions.find(
              (item) => item?.question_id === res.data.question?.question_id,
            ).answer;
            return;
          } else {
            // Track the answer we just submitted on previousQuestion
            let answer;
            if (this.previousQuestion?.question_type === "select") {
              const selectedOption =
                this.previousQuestion.options?.[this.currentAnswer];
              answer = selectedOption ? selectedOption.option_text : null;
            } else {
              answer = this.currentAnswer;
            }
            const question = { ...this.previousQuestion, answer };
            const index = this.answeredQuestions.findIndex(
              (item) => item.question_id === this.previousQuestion?.question_id,
            );
            if (index !== -1) {
              this.answeredQuestions[index] = question;
            } else if (this.previousQuestion) {
              this.answeredQuestions.push(question);
            }
          }

          this.currentAnswer = this.computeInitialAnswer(res.data.question);
        } else if (res.data.err && res.data.err.includes("3994")) {
          this.onLogout?.();
          this.navigate("/login");
        }
      } catch (err) {
        console.error(err);
        this.isAnswerQuestionLoading = false;
        if (isEmbedded()) this.isSelectedViewLoading = false;
      }
    },

    // Initial currentAnswer for a freshly fetched question. Mirrors up-spa's
    // per-question-type rehydration logic.
    computeInitialAnswer(question) {
      if (!question) return null;

      if (["location_detection", "text"].includes(question.question_type)) {
        return true;
      }

      if (question.question_type === "confirm") {
        const answer = {};
        question.options.forEach((option) => {
          answer[option.field_key] =
            question.previous_answer?.[option.field_key] ?? option.data ?? "";
        });
        return answer;
      }

      if (
        question.previous_answer &&
        ["text_input", "text_area", "select"].includes(question.question_type)
      ) {
        return question.previous_answer;
      }

      if (question.question_type === "slider") {
        if (question.previous_answer) {
          return question.previous_answer;
        }
        const answer = {};
        question.sliders?.forEach((s) => {
          answer[s.slider_text] = s.options[0];
        });
        return answer;
      }

      return null;
    },

    setAnswer(val) {
      this.currentAnswer = val;
    },
  },
};
</script>

<style lang="less">
#SessionBooking {
  padding: 70px 0;
  min-height: 100%;
  background-color: #fff;

  button:focus:not(:focus-visible) {
    outline: none;
  }

  .btn-home {
    width: 100%;
    max-width: 648px;
    color: #fff;
    letter-spacing: normal;
    border: 0;
    background-color: var(--brand-primary);
    border-radius: 12px;
    margin: 0 auto;
  }

  .btn-previous {
    border: 1px solid var(--brand-primary);
    padding: 16px;
    color: var(--brand-primary);
    font-family: var(--font-bold, "DMSans Bold");
    background-color: #fff;
    letter-spacing: normal;
    border-radius: 15px !important;

    &:focus {
      background-color: #f6f6f6;
    }
  }

  .btn-continue {
    padding: 16px;
    color: #fff;
    font-family: var(--font-bold, "DMSans Bold");
    background-color: var(--brand-primary);
    letter-spacing: normal;
    border-radius: 15px !important;

    &:disabled {
      background-color: #aeb0bd;
    }
  }

  .error-message {
    text-align: center;
    color: #fd3d3d;
    font-size: 1.6rem;
    font-family: var(--font-regular, "DMSans");
    margin: 20px auto;
  }

  .progress-bar-container {
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 800px;
    padding: 0 20px;
    margin: 0 auto;

    .progress-bar-outer {
      max-width: 650px;
      background-color: #dbdbdb;
      height: 6px;
      border-radius: 5px;
      margin-bottom: 40px;
      overflow: hidden;

      .progress-bar-inner {
        background-color: var(--brand-primary);
        border-radius: 6px;
        height: 100%;
      }
    }
  }

  .dmh-start-btn-container {
    gap: 24px;
    margin-top: 32px;
  }

  .dmh-start-container {
    max-width: 650px;
    margin: 0 auto;

    .warning-title {
      color: #594b00;

      .fa {
        color: #594b00;
      }
    }

    .warning-text {
      color: #1e1e22;
    }
    .warning-container {
      border-radius: 16px;
      margin-top: 20px;
      background-color: #ffee99;
      font-size: 1.6rem;
      padding: 25px;
      h4 {
        font-size: 1.8rem;
      }
      .fa-times {
        width: 48px;
        height: 48px;
        background-color: transparent !important;
        padding: 14px 18px;
        border-radius: 8px;
      }
    }
    .prompt-container {
      margin-top: 20px;
      padding: 25px;
      width: 100%;
      border-radius: 16px;
      background-color: white;
    }

    .dmh-start-title {
      width: 100%;
      text-align: left;
      font-size: 2.2rem;
      letter-spacing: 0;
      color: #1e1e22;
      font-family: var(--font-bold, "DMSans Bold");
      margin-bottom: 32px;
    }

    .dmh-start-subheading {
      color: #1e1e22;
      align-self: flex-start;
      font-size: 1.8rem;
      margin-bottom: 32px;
    }

    .dmh-options-container {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      width: 100%;
    }
  }

  .callback-complete-container,
  .callback-error-container {
    max-width: 440px;
    margin: 0 auto;

    .fa-check-circle {
      font-size: 8rem;
      color: #24a148;
    }

    .fa-exclamation-circle {
      font-size: 8rem;
      color: #da1e28;
    }

    .title {
      font-family: var(--font-bold, "DMSans Bold");
      color: #1e1e22;
      font-size: 2.4rem;
    }

    .paragraph {
      font-family: var(--font-regular, "DMSans");
      color: #1e1e22;
      font-size: 1.8rem;
      text-align: center;
      margin-bottom: 30px;
    }
  }

  .dmh-complete-container {
    font-size: 1.8rem;

    h3 {
      text-align: center;
      font-size: 3.8rem;
      letter-spacing: 0;
      font-family: var(--font-bold, "DMSans Bold");
    }

    p {
      padding-bottom: 30px;
      max-width: 450px;
      text-align: center;
    }
  }

  @media only screen and (max-width: 576px) {
    .dmh-start-btn-container {
      gap: 16px;
    }
  }
}
</style>
